// ============================================================
// TMR Portal Admin — a moldura de um diálogo com formulário
// ============================================================
import type { MouseEvent, ReactNode } from 'react';
import { Icons, StrokeIcon } from '../lib/icons';
import { OverlayPortal } from './OverlayPortal';

/** Props for {@link ModalShell}. */
export interface ModalShellProps {
  title: string;
  /** One line under the title saying what is being acted on — the rate, the band, the current value. */
  lead?: string;
  /** The panel's width. The default fits a form; a dialog that also shows a comparison asks for more. */
  width?: number;
  onClose: () => void;
  children: ReactNode;
}

/**
 * The frame every rate dialog is drawn in: overlay, panel, title, close.
 *
 * It lived inside `RateFormModal` while the rate dialogs of one Empresa were its only users. The global
 * template's form is the second consumer, so it moved here rather than being imported across features —
 * the rule is a real second consumer, not an anticipated one (skill `frontend-architecture`, Regra 2).
 *
 * Distinct from {@link ConfirmDialog}, which is not a frame but a whole dialog: that one owns its buttons
 * and its pending state because every confirmation has the same two actions. A form does not.
 */
export function ModalShell({ title, lead, width = 520, onClose, children }: ModalShellProps) {
  const stop = (event: MouseEvent) => event.stopPropagation();

  return (
    <OverlayPortal>
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 90,
          background: 'rgba(28,30,28,0.32)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
        }}
      >
        <div
          onClick={stop}
          style={{
            width,
            maxWidth: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            background: 'var(--cream-50)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: 'var(--shadow-lg)',
            padding: 24,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: 17, margin: 0, letterSpacing: '-0.01em' }}>
                {title}
              </h3>
              {lead && (
                <p style={{ margin: '6px 0 0', fontSize: 13.5, color: 'var(--fg-muted)', lineHeight: 1.5 }}>
                  {lead}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Fechar"
              style={{ background: 'transparent', border: 0, cursor: 'pointer', color: 'var(--fg-muted)', padding: 4 }}
            >
              <StrokeIcon d={Icons.x} size={18} />
            </button>
          </div>

          <div style={{ marginTop: 18 }}>{children}</div>
        </div>
      </div>
    </OverlayPortal>
  );
}
